/**
 * Case Files Modal
 * 
 * Modal to display and download files associated with a case/report.
 * Shows all uploaded documents with their types and download options.
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, FolderOpen, File, Download, RefreshCw, ExternalLink } from 'lucide-react';
import { api } from '../../../utils/api';
import { useAuth } from '../../../contexts/TestAuthContext';
import { useFocusTrap } from '../../../hooks/useFocusTrap';

interface CaseFile {
  s3_key: string;
  filename: string;
  size: number;
  size_formatted: string;
  last_modified?: string;
  download_url?: string;
  doc_type?: string;
  year?: string;
}

interface CaseFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: string;
  caseName?: string;
}

export default function CaseFilesModal({ isOpen, onClose, claimId, caseName }: CaseFilesModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [files, setFiles] = useState<CaseFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useAuth();

  useEffect(() => {
    if (isOpen && claimId) {
      fetchFiles();
    }
  }, [isOpen, claimId]);

  const fetchFiles = async () => {
    if (!claimId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/reports/${claimId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(data.files || []);
    } catch (err: any) {
      console.error('Error fetching case files:', err);
      setError(err.message || 'Failed to load files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (downloadUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Case Files" className="bg-[var(--color-bg-surface)] rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--color-needs-human-tint)] rounded-lg flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-[var(--color-brand)]" />
            </div>
            <div>
              <h3 className="cs-h2">Case Files</h3>
              {caseName && <p className="cs-meta">{caseName}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--color-text-dim)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
            data-testid="case-files-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-[var(--color-brand)] animate-spin mb-3" />
              <p className="text-[var(--color-text-muted)]">Loading files...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 bg-[var(--color-needs-human-tint)] rounded-full flex items-center justify-center mb-3">
                <X className="w-6 h-6 text-[var(--color-brand)]" />
              </div>
              <p className="text-[var(--color-text-secondary)] font-medium">Error loading files</p>
              <p className="text-sm text-[var(--color-text-muted)] mt-1">{error}</p>
              <button
                onClick={fetchFiles}
                className="mt-4 px-4 py-2 text-sm font-medium text-[var(--color-brand)] hover:bg-[var(--color-needs-human-tint)] rounded-lg transition-colors"
              >
                Try again
              </button>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FolderOpen className="w-12 h-12 text-[var(--color-text-ghost)] mb-3" />
              <p className="text-[var(--color-text-muted)] font-medium">No files found</p>
              <p className="text-sm text-[var(--color-text-dim)] mt-1 text-center">
                Files uploaded for this case will appear here.<br />
                Document data has been extracted and saved.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {files.map((file, idx) => (
                <div
                  key={file.s3_key || idx}
                  className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg flex items-center justify-center flex-shrink-0">
                      <File className="w-5 h-5 text-[var(--color-text-dim)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--color-text-primary)] truncate" title={file.filename}>
                        {file.filename}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
                        {file.doc_type && (
                          <span className="px-1.5 py-0.5 bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] rounded">
                            {file.doc_type}
                          </span>
                        )}
                        {file.year && (
                          <span className="px-1.5 py-0.5 bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] rounded">
                            {file.year}
                          </span>
                        )}
                        {file.size_formatted && file.size_formatted !== 'N/A' && (
                          <span>{file.size_formatted}</span>
                        )}
                        {file.last_modified && (
                          <span>{new Date(file.last_modified).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {file.download_url ? (
                    <button
                      onClick={() => downloadFile(file.download_url!, file.filename)}
                      className="flex-shrink-0 ml-3 px-3 py-1.5 text-sm font-medium text-[var(--color-brand)] hover:text-[var(--color-brand-hover)] hover:bg-[var(--color-needs-human-tint)] rounded-lg transition-colors flex items-center gap-1.5"
                      data-testid={`download-file-${idx}`}
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  ) : (
                    <span className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs text-[var(--color-text-dim)] italic">
                      Data extracted
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Modal Footer */}
        <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] rounded-b-xl">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--color-text-muted)]">
              {files.length} file{files.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg font-medium hover:bg-[var(--color-bg-hover)] transition-colors"
              data-testid="case-files-modal-done"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
